/**
 * The placement dialog: opens on a ground click with the coordinates, beside
 * the clicked point when its screen position is known (so the ghost stays in
 * view), edits the pending spec (name, diameter, bands, minimum elevation,
 * daily hours) and places the antenna with "Place and re-run". Valid edits go
 * straight to the store so the ghost ring on the globe follows them. Esc,
 * Cancel and the close button cancel placement; clicks outside do nothing (a
 * fast double click on the globe would otherwise dismiss the dialog it just opened).
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Dialog } from 'radix-ui';
import { MapPinPlus, X } from 'lucide-react';
import { IconButton, TextButton } from '@/components/ui/primitives';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/cn';
import { computeCoverageRadiusKm } from '../engine/geometry';
import { getGlobeEngine } from '../globe/engineHandle';
import { useLoader } from '../store';
import { commitPlacement } from './flow';
import {
  PLACEMENT_LIMITS,
  antennaFromPlacement,
  bandOptions,
  dialogLeftFor,
  formFromSpec,
  previewAltitudeKm,
  specFromForm,
  type PlacementForm,
} from './placement';
import { usePlacement, type PlacementTarget } from './store';

const inputClass =
  'w-full rounded-control border border-hairline bg-inset px-2.5 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none';

/** anchorX: the click's clientX when known; the dialog then opens on the far side of it, else centred. */
export function PlacementDialog({ anchorX }: { anchorX: number | null }) {
  const target = usePlacement((s) => s.target);
  const left = anchorX !== null ? dialogLeftFor(anchorX, window.innerWidth) : null;
  return (
    <Dialog.Root
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) usePlacement.getState().cancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
        <Dialog.Content
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          // Esc closes the dialog only; the shell's own Esc (clear selection) does not also run.
          onEscapeKeyDown={(e) => e.stopPropagation()}
          style={left !== null ? { left } : undefined}
          className={cn(
            'fixed top-1/2 z-50 flex w-[min(400px,calc(100vw-32px))] -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none',
            left === null && 'left-1/2 -translate-x-1/2',
          )}
        >
          {target && <PlacementFormBody target={target} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-ink-3">{label}</span>
      {children}
    </label>
  );
}

function PlacementFormBody({ target }: { target: PlacementTarget }) {
  const pending = usePlacement((s) => s.pending);
  const issuedIds = usePlacement((s) => s.issuedIds);
  const dataset = useLoader((s) => s.dataset);
  // Initialised once per opening (the content unmounts when the dialog closes).
  const [form, setForm] = useState<PlacementForm>(() => formFromSpec(usePlacement.getState().pending));

  const { spec, problems } = specFromForm(pending, form);
  const options = useMemo(
    () => bandOptions(dataset?.missions ?? [], dataset?.antennas ?? [], form.bands),
    [dataset, form.bands],
  );
  const altKm = previewAltitudeKm(spec.bands, dataset?.missions ?? []);
  // What "Place and re-run" would add, for the name placeholder and the summary line.
  const preview =
    problems.length === 0 && dataset
      ? antennaFromPlacement(spec, target.lat, target.lon, [
          ...dataset.antennas.map((a) => a.id),
          ...issuedIds,
        ])
      : null;

  const update = (patch: Partial<PlacementForm>) => {
    const next = { ...form, ...patch };
    setForm(next);
    const parsed = specFromForm(usePlacement.getState().pending, next);
    if (parsed.problems.length === 0) usePlacement.getState().setPending(parsed.spec);
  };

  const toggleBand = (band: string, on: boolean) =>
    update({ bands: on ? [...form.bands, band] : form.bands.filter((b) => b !== band) });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = specFromForm(usePlacement.getState().pending, form);
    if (parsed.problems.length > 0) return;
    usePlacement.getState().setPending(parsed.spec);
    try {
      commitPlacement(getGlobeEngine());
    } catch (err) {
      console.error('[place] placing the antenna failed:', err);
      toast('error', `Could not place the antenna: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const L = PLACEMENT_LIMITS;
  const elevationOk =
    Number.isFinite(spec.elevationMinDeg) &&
    spec.elevationMinDeg >= L.elevationMinDeg.min &&
    spec.elevationMinDeg <= L.elevationMinDeg.max;
  const radiusKm = elevationOk ? Math.round(computeCoverageRadiusKm(altKm, spec.elevationMinDeg)) : null;

  return (
    <form onSubmit={submit} className="flex flex-col">
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-hairline px-4">
        <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-ink">
          <MapPinPlus className="h-4 w-4 text-accent" />
          Place antenna
        </Dialog.Title>
        <Dialog.Close asChild>
          <IconButton label="Cancel (Esc)">
            <X className="h-4 w-4" />
          </IconButton>
        </Dialog.Close>
      </div>

      <div className="flex flex-col gap-3 px-4 py-3">
        <Dialog.Description className="leading-snug text-ink-2">
          Adds the dish to the what-if and re-runs the analysis. It is not saved to the network data.
        </Dialog.Description>

        <div className="flex items-baseline justify-between rounded-control border border-hairline bg-inset/60 px-3 py-2">
          <span className="text-[10px] uppercase tracking-wider text-ink-3">Location</span>
          <span className="font-mono text-ink">
            <output data-testid="place-lat">{target.lat.toFixed(2)}</output>,{' '}
            <output data-testid="place-lon">{target.lon.toFixed(2)}</output>
          </span>
        </div>

        <Field label="Name">
          {/* Focused on open (radix would focus the close button first), so Enter places the antenna. */}
          <input
            type="text"
            autoFocus
            value={form.name}
            maxLength={L.nameLength}
            placeholder={preview?.name ?? 'Placed antenna'}
            onChange={(e) => update({ name: e.target.value })}
            className={inputClass}
          />
        </Field>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Diameter (m)">
            <input
              type="number"
              inputMode="decimal"
              min={L.diameterM.min}
              max={L.diameterM.max}
              step={0.1}
              value={form.diameterM}
              onChange={(e) => update({ diameterM: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Min elev. (deg)">
            <input
              type="number"
              inputMode="decimal"
              min={L.elevationMinDeg.min}
              max={L.elevationMinDeg.max}
              step={0.5}
              value={form.elevationMinDeg}
              onChange={(e) => update({ elevationMinDeg: e.target.value })}
              className={inputClass}
            />
          </Field>
          <Field label="Daily hours">
            <input
              type="number"
              inputMode="numeric"
              min={L.maxHoursPerDay.min}
              max={L.maxHoursPerDay.max}
              step={1}
              value={form.maxHoursPerDay}
              onChange={(e) => update({ maxHoursPerDay: e.target.value })}
              className={inputClass}
            />
          </Field>
        </div>

        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-[10px] uppercase tracking-wider text-ink-3">Bands</legend>
          <div className="flex flex-wrap gap-1.5">
            {options.map((band) => {
              const on = form.bands.includes(band);
              return (
                <label
                  key={band}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 rounded-control border px-2 py-1',
                    on ? 'border-accent/50 text-ink' : 'border-hairline text-ink-2 hover:text-ink',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => toggleBand(band, e.target.checked)}
                    className="h-3.5 w-3.5 accent-[var(--color-accent)]"
                  />
                  {band}
                </label>
              );
            })}
          </div>
        </fieldset>

        <div className="leading-snug text-ink-2">
          {radiusKm !== null && (
            <div>
              Coverage radius about {radiusKm.toLocaleString('en-US')} km for satellites up to{' '}
              {Math.round(altKm).toLocaleString('en-US')} km at {spec.elevationMinDeg} deg.
            </div>
          )}
          <div className="text-ink-3">
            Maintenance {pending.maintenanceWindowHrs} h a day from 00:00Z.
            {preview && (
              <>
                {' '}
                Adds <span data-testid="place-short-name">{preview.shortName}</span>.
              </>
            )}
          </div>
        </div>

        {problems.length > 0 && (
          <ul className="flex flex-col gap-0.5 text-warning" role="alert">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-hairline px-4 py-3">
        <Dialog.Close asChild>
          <TextButton>Cancel</TextButton>
        </Dialog.Close>
        <TextButton
          type="submit"
          disabled={problems.length > 0}
          className="flex items-center gap-1 bg-accent-soft text-accent"
        >
          <MapPinPlus className="h-3.5 w-3.5" />
          Place and re-run
        </TextButton>
      </div>
    </form>
  );
}
